import { useEffect } from 'react';
import { DollarSign } from 'lucide-react';
import type { PlanStepProps } from '../types';

export function PricingStep({ data, onChange, onValidChange }: PlanStepProps) {
  useEffect(() => {
    const isValid = data.basePrice >= 0;
    onValidChange(isValid);
  }, [data.basePrice, onValidChange]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(price);
  };

  const presetPrices = [0, 99, 199, 299, 499, 999];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Precio del Plan
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Define el precio {data.billingCycle === 'MONTHLY' ? 'mensual' : 'anual'} del plan
        </p>
      </div>

      <div className="space-y-6">
        {/* Price Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Precio Base ({data.billingCycle === 'MONTHLY' ? '/mes' : '/año'})
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
              <DollarSign className="w-5 h-5 text-gray-400" />
            </div>
            <input
              type="number"
              min={0}
              step={0.01}
              value={data.basePrice}
              onChange={(e) => onChange({ basePrice: parseFloat(e.target.value) || 0 })}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg
                       bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-xl font-semibold
                       focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Usa 0 para un plan gratuito
          </p>
        </div>

        {/* Quick Select */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Selección rápida
          </label>
          <div className="grid grid-cols-3 gap-2">
            {presetPrices.map((price) => (
              <button
                key={price}
                type="button"
                onClick={() => onChange({ basePrice: price })}
                className={`p-3 border-2 rounded-lg text-center transition-all ${
                  data.basePrice === price
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                }`}
              >
                <span className={`font-semibold ${
                  data.basePrice === price
                    ? 'text-brand-700 dark:text-brand-300'
                    : 'text-gray-800 dark:text-white'
                }`}>
                  {price === 0 ? 'Gratis' : formatPrice(price)}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Trial Days */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Días de Prueba
          </label>
          <input
            type="number"
            min={0}
            value={data.trialDays}
            onChange={(e) => onChange({ trialDays: parseInt(e.target.value) || 0 })}
            className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg
                     bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                     focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          />
          <p className="text-xs text-gray-500 mt-1">
            Usa 0 para no ofrecer periodo de prueba
          </p>
        </div>

        {/* Preview */}
        <div className="p-6 bg-gradient-to-br from-brand-50 to-brand-100 dark:from-brand-900/20 dark:to-brand-900/10 rounded-xl">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Vista previa</p>
          <div className="flex items-baseline gap-1">
            <span className="text-4xl font-bold text-gray-900 dark:text-white">
              {formatPrice(data.basePrice)}
            </span>
            <span className="text-gray-500 dark:text-gray-400">
              /{data.billingCycle === 'MONTHLY' ? 'mes' : 'año'}
            </span>
          </div>
          {data.billingCycle === 'YEARLY' && data.basePrice > 0 && (
            <p className="text-sm text-gray-500 mt-2">
              Equivale a {formatPrice(data.basePrice / 12)}/mes
            </p>
          )}
          {data.trialDays > 0 && (
            <p className="text-sm text-emerald-600 dark:text-emerald-400 mt-2">
              + {data.trialDays} días de prueba gratis
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
